import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useQueryPagination from 'hooks/useQueryPagination'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { Flex, H1WithBack, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { convertToDate, formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import { clearFuelData, getDispenses } from 'features/fuelManagement/fuelManagementSlice'
import { FilterEnum, IFilter, LinkEnum } from 'components/Filter/SingleFilter'
import { fuelTypes } from './fuelData'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { endOfWeek, startOfWeek } from 'date-fns'
import { MachineFuelLink } from './Machines/MachineFuelLink'
import { IFuelOpsDispenseRow } from 'types/fuelOps.interface'
import { formatGhs } from './Shared/fuelFormatters'
import { mapDispenseToRow } from './Shared/fuelApiMappers'
import { AuthorizedDrawersPanel } from './Dispenses/AuthorizedDrawersPanel'
import { DrumFloatPanel } from './Dispenses/DrumFloatPanel'

const dispenseCostGhs = (item: IFuelOpsDispenseRow): number => item.costGhs ?? 0

const columns: IHonestTableColumn<IFuelOpsDispenseRow>[] = [
  { id: 'day', header: 'DAY', cell: (item) => item.dayLabel },
  { id: 'time', header: 'TIME', cell: (item) => item.time },
  { id: 'machine', header: 'MACHINE / ISSUED TO', cell: (item) => <MachineFuelLink name={item.machine} machineId={item.machineId} /> },
  { id: 'operator', header: 'OPERATOR', cell: (item) => item.operator },
  {
    id: 'type',
    header: 'TYPE',
    cell: (item) => (item.dispenseType === 'DRUM'
      ? (
        <Flex center gap='0.5rem'>
          <StatusPill status='neutral'>DRUM</StatusPill>
          {item.signatureRef && <OtherText grey>{item.signatureRef}</OtherText>}
        </Flex>
      )
      : <OtherText grey>Machine</OtherText>),
  },
  {
    id: 'liters',
    header: 'LITRES',
    cell: (item) => <BodyText weight='bold'>{`${formatNumbersForDisplay(item.liters)} L`}</BodyText>,
  },
  { id: 'cost', header: 'COST', cell: (item) => formatGhs(dispenseCostGhs(item)) },
  { id: 'tank', header: 'SOURCE', cell: (item) => item.tank },
  {
    id: 'smu',
    header: 'SMU AT FILL',
    cell: (item) => (item.smuHours != null ? formatNumbersForDisplay(item.smuHours) + ' h' : '—'),
  },
  {
    id: 'auth',
    header: 'AUTH',
    cell: (item) => (item.authStatus === 'UNAUTHORIZED'
      ? <StatusPill status='danger' dot>UNAUTHORIZED</StatusPill>
      : <OtherText grey>—</OtherText>),
  },
  {
    id: 'contractor',
    header: 'CONTRACTOR',
    defaultVisible: false,
    cell: (item) => item.contractor || '—',
  },
  {
    id: 'flag',
    header: 'FLAG',
    minWidth: '14rem',
    cell: (item) => (item.flag
      ? <StatusPill status={item.flag.tone} dot={item.flag.tone === 'danger'}>{item.flag.label}</StatusPill>
      : <OtherText grey>—</OtherText>),
  },
]

export const FuelDispenses = () => {
  const searchParams = new URLSearchParams(window.location.search)
  const { dispenses } = useSelector((store: RootState) => store.fuelManagement)
  const [params, setParams] = useState<any>(getParams({
    location, ap: {
      dateFrom: searchParams.get('dateFrom') ? new Date(searchParams.get('dateFrom')) : startOfWeek(new Date(), { weekStartsOn: 1 }),
      dateTo: searchParams.get('dateTo') ? new Date(searchParams.get('dateTo')) : endOfWeek(new Date(), { weekStartsOn: 1 }),
    },
  }))

  useQueryPagination({ fetchFunctions: [getDispenses], cleanUpFunctions: [clearFuelData], params })

  const rows: IFuelOpsDispenseRow[] = useMemo(
    () => (dispenses?.data?.length ? dispenses.data.map(mapDispenseToRow) : []),
    [dispenses],
  )

  const setDateFrom = (value) => {
    setParams((prev) => {
      const copy = structuredClone(prev)
      copy.dateFrom = convertToDate(value)
      return copy
    })
  }

  const setDateTo = (value) => {
    setParams((prev) => {
      const copy = structuredClone(prev)
      copy.dateTo = convertToDate(value)
      return copy
    })
  }

  const filters: IFilter[] = [
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Fuel Type',
      state: null,
      propName: 'fuelTypeId',
      sd: true,
      single: true,
      filterOptions: fuelTypes.map((s) => ({ name: s.label, id: s.value.toString() })),
    },
    {
      type: FilterEnum.ASYNC_SELECT,
      link: LinkEnum.MACHINES,
      name: 'Machine',
      state: null,
      single: true,
      propName: 'machineId',
    },
  ]

  return (
    <>
      <NavBar filterOptions={filters} enableTemporaryManualClosing setParams={setParams} params={params} />
      <PageWrapper>
        <Flex width='100%' between>
          <H1WithBack title='Dispenses' arrow />
          <Flex gap='1rem'>
            <CalendarInput
              maxDate={new Date(params.dateTo)}
              labelText='Start date'
              defaultValue={new Date(params.dateFrom || new Date())}
              onChange={(v) => setDateFrom(v)}
            />
            <CalendarInput
              minDate={new Date(params.dateFrom)}
              labelText='End date'
              defaultValue={new Date(params.dateTo || new Date())}
              onChange={(v) => setDateTo(v)}
            />
          </Flex>
        </Flex>
        <Flex mt='4' mb='16'>
          <OtherText grey>Full dispense log across tanks, machines, drums and operators — only exceptions shout</OtherText>
        </Flex>
        {!rows.length ? (
          <NoItems title='Dispenses' />
        ) : (
          <HonestTable columns={columns} data={rows} keyField='id' columnToggle />
        )}
        <Flex column gap='1rem' mt='16' width='100%'>
          <AuthorizedDrawersPanel />
          <DrumFloatPanel />
        </Flex>
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(dispenses?.totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
